import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Globe } from "lucide-react";
import { NAV as NAV_EN } from "../mock";
import { NAV as NAV_DE } from "../mock.de";
import { getLang, otherLangPath } from "../i18n";
import BrandMark from "./BrandMark";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
} from "./ui/sheet";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const lang = getLang(location.pathname);
  const NAV = lang === "de" ? NAV_DE : NAV_EN;
  const homePath = lang === "de" ? "/de" : "/";
  const switchPath = otherLangPath(location.pathname);
  const langLabels = lang === "de" ? { self: "DE", other: "EN" } : { self: "EN", other: "DE" };
  const languageWord = lang === "de" ? "Sprache" : "Language";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => location.pathname === to;
  const dropdownActive = NAV.links
    .find((l) => l.dropdown)
    ?.dropdown.some((d) => isActive(d.to));

  const LangSwitcher = ({ style = {} }) => (
    <span className="lang-pill" style={style} data-testid="lang-switcher">
      <Globe size={15} />
      <span className="lang-current">{langLabels.self}</span>
      <span className="lang-divider">/</span>
      <Link to={switchPath} className="lang-alt" data-testid={`lang-switch-${langLabels.other.toLowerCase()}`}>
        {langLabels.other}
      </Link>
    </span>
  );

  return (
    <header className={`nav-shell ${scrolled ? "scrolled" : ""}`}>
      <div className="container-page">
        <div className="nav-inner">
          <Link to={homePath} className="brand" aria-label={`${NAV.brand} — Home`}>
            <BrandMark />
            <span className="brand-name">{NAV.brand}</span>
          </Link>

          <nav className="nav-links" aria-label="Primary">
            {NAV.links.map((link) =>
              link.dropdown ? (
                <div className="nav-dropdown" key={link.label}>
                  <button
                    className={`nav-link ${dropdownActive ? "active" : ""}`}
                    aria-haspopup="true"
                  >
                    {link.label}
                    <ChevronDown size={15} style={{ marginLeft: 4, marginBottom: -2 }} />
                  </button>
                  <div className="nav-dropdown-menu" role="menu">
                    {link.dropdown.map((d) => (
                      <Link key={d.to} to={d.to} className="nav-dropdown-item" role="menuitem">
                        <span className="dd-title">{d.label}</span>
                        <span className="dd-desc">{d.desc}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-link ${isActive(link.to) ? "active" : ""}`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="nav-right">
            <LangSwitcher />

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="nav-toggle" aria-label="Open menu">
                  <Menu size={24} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[86vw] max-w-[380px] overflow-y-auto">
                <SheetTitle className="sr-only">{NAV.brand}</SheetTitle>
                <div className="flex items-center gap-2 pb-2">
                  <BrandMark />
                  <span className="brand-name">{NAV.brand}</span>
                </div>
                <div className="m-nav">
                  {NAV.links.map((link) =>
                    link.dropdown ? (
                      <React.Fragment key={link.label}>
                        <div className="m-nav-group-title">{link.label}</div>
                        {link.dropdown.map((d) => (
                          <SheetClose asChild key={d.to}>
                            <Link to={d.to} className="m-nav-link sub">
                              {d.label}
                            </Link>
                          </SheetClose>
                        ))}
                      </React.Fragment>
                    ) : (
                      <SheetClose asChild key={link.to}>
                        <Link to={link.to} className="m-nav-link">
                          {link.label}
                        </Link>
                      </SheetClose>
                    )
                  )}
                </div>
                <div className="m-nav-group-title">{languageWord}</div>
                <LangSwitcher style={{ display: "inline-flex" }} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
